/**
 * AR session actions. The session lives in the store (not in a component) because every
 * Run entry point — the toolbar play button, Sketch > Run, Cmd+Enter, the mobile button —
 * has to be able to push the current sketch to the phone, and only the toolbar mounts the
 * AR button.
 */
import * as ActionTypes from '../../../constants';
import {
  ensureArSessionId,
  publishArSession,
  publishArSettings,
  publishSliderValues,
  subscribeArSession
} from '../../../firebase/arSession';
import { dispatchMessage, MessageTypes } from '../../../utils/dispatcher';
import { getIsUserOwner } from '../selectors/users';

/**
 * Does the signed-in user own the open sketch? An ownerless project is a brand-new, unsaved
 * sketch whose owner-to-be is the current user, so it counts as owned.
 *
 * This is the switch between the two AR session kinds: the project's shared `arSessionId`
 * (owner) versus the instructor's own private one (see `ensureArSessionId`).
 */
function ownsOpenProject(state) {
  return !state.project.owner || getIsUserOwner(state);
}

// Firestore listener on the live session doc, forwarding remote changes (e.g. slider
// values nudged from the AR viewer) down to the preview iframe. Module-level because this
// is a thunk file, not a component: started when a session becomes active (openArSession),
// stopped when it ends (stopArSession).
let unsubscribeSessionDoc = null;

// Latest stored slider values from the live session doc, cached here (not in Redux)
// so `startSketch` can inject them into a freshly-built preview iframe synchronously,
// BEFORE the sketch's setup() registers its sliders (see getLatestSliderValues /
// EmbedFrame's window.__arSliderInitialValues). The subscription keeps this current;
// it resets to {} whenever the session subscription stops.
let latestSliderValues = {};

/**
 * The last-known `sliderValues` map from the active AR session doc ({} when none).
 * Consumed by the run pipeline (`startSketch`) to seed the preview's slider registry
 * so stored values load on the first Run — persist-by-default, matching the AR viewer.
 */
export function getLatestSliderValues() {
  return latestSliderValues;
}

function stopSessionSubscription() {
  if (unsubscribeSessionDoc) {
    unsubscribeSessionDoc();
    unsubscribeSessionDoc = null;
  }
  latestSliderValues = {};
}

function startSessionSubscription(sessionId) {
  stopSessionSubscription();
  unsubscribeSessionDoc = subscribeArSession(sessionId, (data) => {
    if (data && data.sliderValues) {
      latestSliderValues = data.sliderValues;
      dispatchMessage({
        type: MessageTypes.AR_SLIDER,
        payload: { values: data.sliderValues }
      });
    }
  });
}

/**
 * Start watching the current project's AR session doc as soon as the project loads —
 * independent of opening the AR modal — so its stored `sliderValues` are cached (and
 * forwarded live to the preview) and load on the very first Run. Without this the value
 * cache stayed empty until the user pressed AR, so a plain load+Run always used code
 * defaults. No arSessionId (project never used AR) → tear down any prior watch and reset
 * the cache. Idempotent: `startSessionSubscription` stops any existing listener first, so
 * a later `openArSession` restarting it on the same id is harmless.
 */
export function watchArSessionValues() {
  return (dispatch, getState) => {
    const state = getState();
    // Owner only: `project.arSessionId` is the OWNER's session. An instructor viewing a
    // student's sketch has their own session (started by openArSession) and must not be
    // fed — or write back to — the student's stored slider values.
    const sessionId = ownsOpenProject(state) ? state.project.arSessionId : null;
    if (!sessionId) {
      stopSessionSubscription();
      return;
    }
    startSessionSubscription(sessionId);
  };
}

export function setArSessionBusy(busy) {
  return { type: ActionTypes.AR_SESSION_BUSY, busy };
}

export function setArSessionError(error) {
  return { type: ActionTypes.AR_SESSION_ERROR, error };
}

export function closeArSessionModal() {
  return { type: ActionTypes.AR_SESSION_CLOSED };
}

/**
 * Record whether the sketch we just published uses `angleMode(DEGREES)`, which the AR
 * bridge cannot honour (it forces radians, so the angles are wrong on the phone only).
 * Drives the warning bar over the code pane.
 *
 * Takes publishArSession's return value, and tolerates it being undefined — a publish
 * path that returns nothing must read as "no degrees", never crash the Run.
 *
 * @param {{ usesDegrees?: boolean }} [published]
 */
export function reportDegreesUsage(published) {
  return {
    type: ActionTypes.AR_DEGREES_USAGE_REPORTED,
    usesDegrees: !!(published && published.usesDegrees)
  };
}

export function dismissDegreesWarning() {
  return { type: ActionTypes.AR_DEGREES_WARNING_DISMISSED };
}

export function stopArSession() {
  return (dispatch) => {
    stopSessionSubscription();
    dispatch({ type: ActionTypes.AR_SESSION_STOPPED });
  };
}

/**
 * Create (or reuse) the project's AR session, then push the current sketch so the phone
 * shows something as soon as the QR code is scanned.
 */
export function openArSession() {
  return async (dispatch, getState) => {
    dispatch(setArSessionError(null));
    dispatch(setArSessionBusy(true));
    try {
      const isOwner = ownsOpenProject(getState());
      const sessionId = await ensureArSessionId(getState().project, {
        isOwner
      });
      // Mirror the (possibly freshly created) token into project state so a second press
      // reuses this session rather than generating and invalidating a new one. Owner only:
      // a non-owner's session is theirs alone (persisted in localStorage, not on the project
      // doc), and writing it here would shadow the student's own token in the open project
      // state — which `publications.ts` reads to inherit their AR settings.
      if (isOwner && getState().project.arSessionId !== sessionId) {
        dispatch({
          type: ActionTypes.SET_PROJECT_AR_SESSION_ID,
          value: sessionId
        });
      }
      dispatch({ type: ActionTypes.AR_SESSION_STARTED, sessionId });
      startSessionSubscription(sessionId);
      const published = await publishArSession({
        sessionId,
        files: getState().files,
        arSettings: getState().preferences.arSettings
      });
      dispatch(reportDegreesUsage(published));
    } catch (e) {
      dispatch(setArSessionError(e.message || String(e)));
      dispatch({ type: ActionTypes.AR_SESSION_OPENED });
    } finally {
      dispatch(setArSessionBusy(false));
    }
  };
}

/**
 * Push the latest code to the phone. A no-op unless AR is open, so Run handlers can
 * dispatch it unconditionally. Publish failures land in session state rather than
 * rejecting — a dead network must not break running the sketch locally.
 */
export function publishArSessionIfActive() {
  return async (dispatch, getState) => {
    const { isActive, sessionId } = getState().arSession;
    if (!isActive || !sessionId) return;
    try {
      const published = await publishArSession({
        sessionId,
        files: getState().files,
        arSettings: getState().preferences.arSettings
      });
      dispatch(reportDegreesUsage(published));
    } catch (e) {
      dispatch(setArSessionError(e.message || String(e)));
    }
  };
}

/**
 * Push the current AR alignment settings to the live session, if any. A no-op unless AR
 * is open, so preference changes can dispatch it unconditionally. Does NOT bump runId —
 * the viewer applies the settings without re-running the sketch. Failures land in session
 * state rather than rejecting.
 */
export function publishArSettingsIfActive() {
  return async (dispatch, getState) => {
    const { isActive, sessionId } = getState().arSession;
    if (!isActive || !sessionId) return;
    try {
      await publishArSettings({
        sessionId,
        arSettings: getState().preferences.arSettings
      });
    } catch (e) {
      dispatch(setArSessionError(e.message || String(e)));
    }
  };
}

/**
 * Push a live slider-value patch to the session, if any (nested field write, no runId
 * bump). A no-op unless AR is open, so the preview's per-slider change handler can
 * dispatch it unconditionally. Failures are swallowed (non-fatal) rather than surfaced
 * as session errors — sliders fire far more often than settings changes, and a dropped
 * nudge shouldn't interrupt the sketch or spam the AR error UI.
 */
export function publishSliderValuesIfActive(patch) {
  return async (dispatch, getState) => {
    const { isActive, sessionId } = getState().arSession;
    if (!isActive || !sessionId) return;
    try {
      await publishSliderValues(sessionId, patch);
    } catch (e) {
      // Non-fatal: keep the sketch running.
    }
  };
}
